import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import { useViewMode } from '../contexts/ViewModeContext';
import { 
  Search, Plus, List, LayoutGrid, Calendar as CalendarIcon, Edit, Trash2, X, 
  MoreVertical, User, CalendarDays, ChevronLeft, ChevronRight, CheckCircle, 
  Clock, AlertTriangle, RefreshCw, CalendarCheck, CalendarClock, Flame, Zap, Briefcase
} from 'lucide-react';
import TaskModal from './TaskModal';
import TaskFilters from './TaskFilters';
import EmptyState from './EmptyState';
import { getLighterColor } from '../lib/clientColors';
import { formatDate } from '../lib/dateUtils';
import { SearchableSelect } from './ui/searchable-select';

// Compute due-date state + matching color/badge to make Overdue / Today / Tomorrow
// visually distinct at a glance across all task cards in this page.
// Returns: { colorClass, pillClass, pill, urgency } where urgency is one of
//   'overdue' | 'today' | 'tomorrow' | 'normal' | 'completed' | 'none'
const getDueStatus = (due_date, status) => {
  if (status === 'Completed') return { urgency: 'completed', colorClass: 'text-neutral-400', pill: null };
  if (!due_date) return { urgency: 'none', colorClass: 'text-neutral-500', pill: null };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const due = new Date(due_date);
  due.setHours(0, 0, 0, 0);
  if (due < today) return {
    urgency: 'overdue', colorClass: 'text-red-600 font-semibold',
    pill: { text: 'OVERDUE', className: 'text-red-700 bg-red-100 border-red-200' }
  };
  if (due.getTime() === today.getTime()) return {
    urgency: 'today', colorClass: 'text-amber-600 font-semibold',
    pill: { text: 'TODAY', className: 'text-amber-700 bg-amber-100 border-amber-200' }
  };
  if (due.getTime() === tomorrow.getTime()) return {
    urgency: 'tomorrow', colorClass: 'text-yellow-600 font-medium',
    pill: { text: 'TOMORROW', className: 'text-yellow-700 bg-yellow-100 border-yellow-200' }
  };
  return { urgency: 'normal', colorClass: 'text-neutral-500', pill: null };
};

// Task Statistics Component with Clickable Filters (Admin - 4 per row desktop, 2 per row mobile)
// Now uses server-side stats for accurate counts across ALL data (not just loaded)
function TaskStatistics({ serverStats, isMobile, activeFilter, onFilterClick }) {
  const stats = [
    { key: 'pending', label: 'Pending Tasks', value: serverStats?.pending || 0, icon: Briefcase, gradient: 'metric-gradient-indigo' },
    { key: 'new', label: 'New Tasks', value: serverStats?.new || 0, icon: Clock, gradient: 'metric-gradient-cyan' },
    { key: 'overdue', label: 'Overdue', value: serverStats?.overdue || 0, icon: AlertTriangle, gradient: 'metric-gradient-red' },
    { key: 'revision', label: 'Revision', value: serverStats?.revision || 0, icon: RefreshCw, gradient: 'metric-gradient-orange' },
    { key: 'today', label: 'Due Today', value: serverStats?.today || 0, icon: CalendarCheck, gradient: 'metric-gradient-emerald' },
    { key: 'tomorrow', label: 'Due Tomorrow', value: serverStats?.tomorrow || 0, icon: CalendarClock, gradient: 'metric-gradient-teal' },
    { key: 'week', label: 'Due This Week', value: serverStats?.week || 0, icon: CalendarIcon, gradient: 'metric-gradient-blue' },
    { key: 'high', label: 'High Priority', value: serverStats?.high || 0, icon: Flame, gradient: 'metric-gradient-rose' },
    { key: 'critical', label: 'Critical', value: serverStats?.critical || 0, icon: Zap, gradient: 'metric-gradient-violet' }
  ];

  if (isMobile) {
    return (
      <div className="grid grid-cols-2 gap-2 mb-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          const isActive = activeFilter === stat.key;
          return (
            <button key={stat.key} onClick={() => onFilterClick(isActive ? null : stat.key)}
              className={`${stat.gradient} rounded-2xl p-3 text-left transition-all active:scale-[0.98] ${isActive ? 'ring-2 ring-white/60 ring-offset-2 shadow-lg' : ''}`}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-lg font-bold text-white">{stat.value}</p>
                  <p className="text-xs text-white/70 truncate">{stat.label}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
      {stats.map((stat) => {
        const Icon = stat.icon;
        const isActive = activeFilter === stat.key;
        return (
          <button key={stat.key} onClick={() => onFilterClick(isActive ? null : stat.key)}
            className={`${stat.gradient} rounded-2xl p-4 text-left transition-all hover:scale-[1.02] hover:shadow-lg ${isActive ? 'ring-2 ring-white/60 ring-offset-2 shadow-xl scale-[1.02]' : ''}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-white/70">{stat.label}</p>
                <p className="text-2xl font-bold text-white">{stat.value}</p>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Mobile Task Card Component with Quick Status Update
function MobileTaskCard({ task, onEdit, onDelete, onStatusChange, statusColors, priorityColors, clients }) {
  const statuses = ['New', 'In Progress', 'Review', 'Revision', 'Completed'];

  const handleQuickStatusChange = (newStatus) => {
    if (newStatus !== task.status) {
      onStatusChange(task._id, newStatus);
    }
  };

  // Get next logical status for quick advance
  const getNextStatus = () => {
    const currentIndex = statuses.indexOf(task.status);
    if (currentIndex < statuses.length - 1) {
      return statuses[currentIndex + 1];
    }
    return null;
  };

  const nextStatus = getNextStatus();

  // Get assignee display name
  const getAssigneeName = () => {
    if (task.assignee_name && task.assignee_name !== 'Unassigned') {
      return task.assignee_name.split(' ')[0];
    }
    if (task.primary_assignee_name) {
      return task.primary_assignee_name.split(' ')[0];
    }
    return 'Unassigned';
  };

  // Check if task is overdue
  const isOverdue = () => {
    if (!task.due_date || task.status === 'Completed') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(task.due_date) < today;
  };

  // Get client color
  const getClientColor = () => {
    if (!task.client_id || !clients) return null;
    const client = clients.find(c => c._id === task.client_id);
    return client?.color || null;
  };

  const clientColor = getClientColor();
  const dueStatus = getDueStatus(task.due_date, task.status);

  return (
    <div 
      className="glass-card rounded-xl mb-3 overflow-hidden"
      style={{ borderLeft: clientColor ? `4px solid ${clientColor}` : undefined }}
    >
      <div className="p-4">
        {/* Top Row: Title + Edit Button */}
        <div className="flex items-start justify-between mb-1">
          <div className="flex-1 pr-2">
            <h3 className="font-semibold text-[#1a1a2e] line-clamp-2">{task.title}</h3>
            {dueStatus.pill && (
              <span className={`inline-block mt-1 px-1.5 py-0.5 rounded border text-[10px] font-bold tracking-wide ${dueStatus.pill.className}`}>
                {dueStatus.pill.text}
              </span>
            )}
          </div>
          <button 
            onClick={(e) => { e.stopPropagation(); onEdit(task); }} 
            className="p-1.5 text-neutral-400 hover:text-pink-600 hover:bg-pink-50 rounded-xl flex-shrink-0"
          >
            <Edit className="w-4 h-4" />
          </button>
        </div>

        {/* Client Name with Color Badge */}
        {task.client_name && (
          <div className="flex items-center gap-1.5 mb-2">
            {clientColor && (
              <span 
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: clientColor }}
              />
            )}
            <p className="text-sm text-neutral-600">{task.client_name}</p>
          </div>
        )}

        {/* Status + Priority Row */}
        <div className="flex items-center gap-2 mb-2">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[task.status]}`}>
            {task.status}
          </span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityColors[task.priority]}`}>
            {task.priority}
          </span>
        </div>

        {/* Due Date + Assignee Row */}
        <div className="flex items-center justify-between text-xs text-neutral-500 mb-3">
          <span className="flex items-center gap-1">
            <User className="w-3 h-3" />
            {getAssigneeName()}
          </span>
          {task.due_date && (
            <span className={`flex items-center gap-1 ${dueStatus.colorClass}`}>
              <CalendarDays className="w-3 h-3" />
              {formatDate(task.due_date)}
            </span>
          )}
        </div>

        {/* Minimal Move to Next Status Button */}
        {nextStatus && (
          <button
            onClick={(e) => { e.stopPropagation(); handleQuickStatusChange(nextStatus); }}
            className="w-full py-2 px-3 bg-pink-500 hover:bg-pink-600 text-white rounded-xl font-medium text-xs flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all"
          >
            <ChevronRight className="w-3.5 h-3.5" />
            Move to {nextStatus}
          </button>
        )}
      </div>
    </div>
  );
}

// Inline Editable Cell Component
function InlineEditCell({ value, type, options, onSave, className }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  
  const handleSave = (override) => {
    const next = override !== undefined ? override : editValue;
    if (next !== value) {
      onSave(next);
    }
    setIsEditing(false);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') { setEditValue(value); setIsEditing(false); }
  };
  
  if (isEditing) {
    if (type === 'select') {
      return (
        <SearchableSelect
          value={editValue}
          onChange={(v) => { setEditValue(v); setTimeout(() => handleSave(v), 0); }}
          triggerClassName="h-8 min-w-[130px] text-sm rounded border-pink-300 bg-white focus:ring-2 focus:ring-brutal-pink/30"
          options={options.map(opt => ({ value: opt, label: opt }))}
        />
      );
    }
    if (type === 'date') {
      return (
        <input type="date" value={editValue || ''} onChange={(e) => setEditValue(e.target.value)} onBlur={handleSave} onKeyDown={handleKeyDown} autoFocus
          className="px-2 py-1 border border-pink-300 rounded text-sm focus:ring-2 focus:ring-brutal-pink/30" />
      );
    }
    return (
      <input type="text" value={editValue || ''} onChange={(e) => setEditValue(e.target.value)} onBlur={handleSave} onKeyDown={handleKeyDown} autoFocus
        className="px-2 py-1 border border-pink-300 rounded text-sm focus:ring-2 focus:ring-brutal-pink/30 w-full" />
    );
  }
  
  return (
    <span onClick={() => { setEditValue(value); setIsEditing(true); }} className={`cursor-pointer hover:bg-pink-50 px-1 py-0.5 rounded ${className || ''}`} title="Click to edit">
      {type === 'date' && value ? formatDate(value) : (value || '-')}
    </span>
  );
}

// Task Row Component with inline editing - Columns: Title, Client, Due Date, Assignee, Status, Priority
function TaskRow({ task, onOpenModal, onInlineUpdate, onDelete, statusColors, priorityColors, clients }) {
  const statuses = ['New', 'In Progress', 'Review', 'Revision', 'Completed'];
  const priorities = ['Low', 'Medium', 'High', 'Critical'];
  
  // Check if task is overdue
  const isOverdue = () => {
    if (!task.due_date || task.status === 'Completed') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(task.due_date) < today;
  };

  // Get client color
  const getClientColor = () => {
    if (!task.client_id || !clients) return null;
    const client = clients.find(c => c._id === task.client_id);
    return client?.color || null;
  };

  const clientColor = getClientColor();
  const dueStatus = getDueStatus(task.due_date, task.status);
  
  return (
    <tr 
      className="hover:bg-white/60 transition-colors group"
      style={{ 
        backgroundColor: clientColor ? getLighterColor(clientColor, 0.05) : undefined,
        borderLeft: clientColor ? `3px solid ${clientColor}` : undefined
      }}
    >
      {/* Task Title with urgency pill */}
      <td className="px-4 py-3">
        <InlineEditCell value={task.title} type="text" onSave={(v) => onInlineUpdate(task._id, { title: v })} className="font-semibold text-[#1a1a2e]" />
        {dueStatus.pill && (
          <span className={`inline-block mt-1 px-1.5 py-0.5 rounded border text-[10px] font-bold tracking-wide ${dueStatus.pill.className}`}>
            {dueStatus.pill.text}
          </span>
        )}
      </td>
      {/* Client with Color Badge */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5">
          {clientColor && (
            <span 
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: clientColor }}
            />
          )}
          <span className="text-neutral-600">{task.client_name || 'N/A'}</span>
        </div>
      </td>
      {/* Due Date */}
      <td className="px-4 py-3">
        <InlineEditCell 
          value={task.due_date} 
          type="date" 
          onSave={(v) => onInlineUpdate(task._id, { due_date: v })} 
          className={dueStatus.colorClass} 
        />
      </td>
      {/* Assignee */}
      <td className="px-4 py-3 text-neutral-600">{task.assignee_name || 'Unassigned'}</td>
      {/* Status */}
      <td className="px-4 py-3">
        <InlineEditCell value={task.status} type="select" options={statuses} onSave={(v) => onInlineUpdate(task._id, { status: v })}
          className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${statusColors[task.status]}`} />
      </td>
      {/* Priority */}
      <td className="px-4 py-3">
        <InlineEditCell value={task.priority} type="select" options={priorities} onSave={(v) => onInlineUpdate(task._id, { priority: v })}
          className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${priorityColors[task.priority]}`} />
      </td>
      {/* Actions */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={() => onOpenModal(task)} className="p-1.5 text-pink-600 hover:bg-pink-50 rounded" title="Edit in Modal"><Edit className="w-4 h-4" /></button>
          <button onClick={() => onDelete(task._id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      </td>
    </tr>
  );
}

// Draggable Kanban Card - Shows: Title, Client, Due Date, Assignee, Priority + OVERDUE
function DraggableKanbanCard({ task, onEdit, onDelete, onDragStart, statusColors, priorityColors, clients }) {
  const [showMenu, setShowMenu] = useState(false);
  
  // Check if task is overdue
  const isOverdue = () => {
    if (!task.due_date || task.status === 'Completed') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(task.due_date) < today;
  };

  // Get client color
  const getClientColor = () => {
    if (!task.client_id || !clients) return null;
    const client = clients.find(c => c._id === task.client_id);
    return client?.color || null;
  };

  const clientColor = getClientColor();
  const dueStatus = getDueStatus(task.due_date, task.status);
  
  return (
    <div 
      draggable 
      onDragStart={(e) => onDragStart(e, task)} 
      className="glass-card p-4 hover:shadow-soft cursor-grab active:cursor-grabbing overflow-hidden"
      style={{ 
        borderLeft: clientColor ? `4px solid ${clientColor}` : undefined,
        backgroundColor: clientColor ? getLighterColor(clientColor, 0.03) : undefined
      }}
    >
      {/* Header with Menu */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <h4 className="font-semibold text-[#1a1a2e] line-clamp-2">{task.title}</h4>
          {dueStatus.pill && (
            <span className={`inline-block mt-1 px-1.5 py-0.5 rounded border text-[10px] font-bold tracking-wide ${dueStatus.pill.className}`}>
              {dueStatus.pill.text}
            </span>
          )}
        </div>
        <div className="relative">
          <button onClick={() => setShowMenu(!showMenu)} className="text-neutral-400 hover:text-neutral-600 p-1"><MoreVertical className="w-4 h-4" /></button>
          {showMenu && (
            <div className="absolute right-0 top-6 bg-white border rounded-xl shadow-lg z-10 py-1 min-w-[100px]">
              <button onClick={() => { setShowMenu(false); onEdit(); }} className="w-full px-4 py-2 text-left text-sm text-neutral-700 hover:bg-white/60 flex items-center gap-2"><Edit className="w-3 h-3" /> Edit</button>
              <button onClick={() => { setShowMenu(false); onDelete(); }} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          )}
        </div>
      </div>
      
      {/* Client with Color Badge */}
      {task.client_name && (
        <div className="mb-2">
          <span 
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium"
            style={{
              backgroundColor: clientColor ? getLighterColor(clientColor, 0.15) : '#F3F4F6',
              color: clientColor || '#374151',
              borderLeft: clientColor ? `3px solid ${clientColor}` : undefined
            }}
          >
            {clientColor && (
              <span 
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: clientColor }}
              />
            )}
            {task.client_name}
          </span>
        </div>
      )}
      
      {/* Priority Badge */}
      <div className="mb-2">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${priorityColors[task.priority]}`}>
          {task.priority}
        </span>
      </div>
      
      {/* Assignee */}
      <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1">
        <User className="w-3 h-3" />
        <span>{task.assignee_name || 'Unassigned'}</span>
      </div>
      
      {/* Due Date */}
      {task.due_date && (
        <div className={`flex items-center gap-2 text-xs ${dueStatus.colorClass}`}>
          <CalendarDays className="w-3 h-3" />
          <span>{formatDate(task.due_date)}</span>
        </div>
      )}
    </div>
  );
}

// Kanban Column with Drop Zone
function KanbanColumn({ status, color, tasks, onEditTask, onDeleteTask, onDragStart, onDrop, isMobile, statusColors, priorityColors, clients }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e) => { e.preventDefault(); setIsDragOver(false); const taskId = e.dataTransfer.getData('taskId'); if (taskId) onDrop(taskId, status); };
  const columnTasks = tasks.filter(t => t.status === status);
  
  return (
    <div className={`soft-card overflow-hidden ${isMobile ? 'min-w-[280px]' : ''}`}>
      <div className={`px-4 py-3 border-b-4 ${color}`}>
        <h3 className="font-bold text-[#1a1a2e] text-base">{status}</h3>
        <p className="text-sm text-neutral-500">{columnTasks.length} tasks</p>
      </div>
      <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
        className={`p-3 space-y-3 ${isMobile ? 'min-h-[300px] max-h-[400px]' : 'min-h-[400px] max-h-[calc(100vh-400px)]'} overflow-y-auto transition-colors ${isDragOver ? 'bg-pink-50 border-2 border-dashed border-pink-300' : 'bg-white/40'}`}>
        {columnTasks.map(task => <DraggableKanbanCard key={task._id} task={task} onEdit={() => onEditTask(task)} onDelete={() => onDeleteTask(task._id)} onDragStart={onDragStart} statusColors={statusColors} priorityColors={priorityColors} clients={clients} />)}
        {columnTasks.length === 0 && <p className="text-neutral-400 text-sm text-center py-8">Drop tasks here</p>}
      </div>
    </div>
  );
}

// Calendar View Component - Mobile Responsive
function TaskCalendarView({ tasks, onEditTask, onCreateTask, isMobile }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = isMobile ? ["S", "M", "T", "W", "T", "F", "S"] : ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const startingDay = firstDayOfMonth.getDay();
  const totalDays = lastDayOfMonth.getDate();
  const today = new Date(); today.setHours(0,0,0,0);

  const getTasksForDate = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return tasks.filter(t => t.due_date === dateStr);
  };

  const statusColors = { 'New': 'bg-gray-400', 'In Progress': 'bg-blue-500', 'Review': 'bg-purple-500', 'Revision': 'bg-yellow-500', 'Completed': 'bg-green-500' };
  const calendarDays = [];
  for (let i = 0; i < startingDay; i++) calendarDays.push(null);
  for (let day = 1; day <= totalDays; day++) calendarDays.push(day);

  const handleDateClick = (day) => {
    if (!day) return;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onCreateTask(dateStr);
  };

  return (
    <div className="glass-card p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <h2 className={`font-bold text-[#1a1a2e] ${isMobile ? 'text-lg' : 'text-xl'}`}>
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <div className="flex items-center gap-1 md:gap-2">
          <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))} 
            className="p-1.5 md:p-2 hover:bg-neutral-100 rounded-xl">
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          <button onClick={() => setCurrentDate(new Date())} 
            className="px-2 py-1 text-xs md:text-sm text-pink-600 hover:bg-pink-50 rounded-xl">
            Today
          </button>
          <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))} 
            className="p-1.5 md:p-2 hover:bg-neutral-100 rounded-xl">
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-neutral-200 rounded-xl overflow-hidden">
        {dayNames.map(d => (
          <div key={d} className={`bg-white/40 text-center py-2 md:py-3 text-xs font-semibold text-neutral-600`}>
            {d}
          </div>
        ))}
        {calendarDays.map((day, i) => {
          const dayTasks = day ? getTasksForDate(day) : [];
          const isToday = day && currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear() && day === today.getDate();
          return (
            <div key={i} onClick={() => handleDateClick(day)}
              className={`bg-white ${isMobile ? 'min-h-[60px] p-1' : 'min-h-[100px] p-2'} ${day ? 'cursor-pointer hover:bg-pink-50' : ''} ${isToday ? 'ring-2 ring-inset ring-pink-500' : ''}`}>
              {day && (<>
                <div className={`text-xs md:text-sm font-medium mb-1 ${isToday ? 'text-pink-600' : 'text-neutral-700'}`}>{day}</div>
                <div className="space-y-0.5 md:space-y-1">
                  {dayTasks.slice(0, isMobile ? 1 : 3).map(task => (
                    <div key={task._id} onClick={(e) => { e.stopPropagation(); onEditTask(task); }}
                      className={`${statusColors[task.status]} text-white text-[10px] md:text-xs px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80`}>
                      {isMobile ? '' : task.title}
                    </div>
                  ))}
                  {dayTasks.length > (isMobile ? 1 : 3) && (
                    <div className="text-[10px] md:text-xs text-neutral-500 font-medium">
                      +{dayTasks.length - (isMobile ? 1 : 3)}
                    </div>
                  )}
                  {isMobile && dayTasks.length > 0 && dayTasks.length <= 1 && (
                    <div className="w-1.5 h-1.5 rounded-full bg-pink-500 mx-auto"></div>
                  )}
                </div>
              </>)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminTasksPage({ initialFilter = null, onClearFilter = () => {} }) {
  const { isMobileView } = useViewMode();
  const [tasks, setTasks] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeView, setActiveView] = useState('list');
  const [activeTab, setActiveTab] = useState('tasks');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showTaskTypeModal, setShowTaskTypeModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingTaskType, setEditingTaskType] = useState(null);
  const [taskTypeForm, setTaskTypeForm] = useState({ name: '', subtypes: [''] });
  const [presetDueDate, setPresetDueDate] = useState(null);
  const [quickFilter, setQuickFilter] = useState(initialFilter);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTasks, setTotalTasks] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const PAGE_SIZE = 50;
  // Server-side statistics - accurate counts from ALL data
  const [serverStats, setServerStats] = useState({
    new: 0, overdue: 0, revision: 0, today: 0, tomorrow: 0, week: 0, high: 0, critical: 0
  });
  const [filters, setFilters] = useState({
    clients: [],
    departments: [],
    employees: [],
    taskTypes: [],
    subtypes: [],
    statuses: [],
    priorities: [],
    dateFrom: '',
    dateTo: ''
  });
  
  // Ref for infinite scroll
  const loadMoreRef = React.useRef(null);
  const containerRef = React.useRef(null);

  // Build query params for server-side filtering
  const buildQueryParams = (page = 1) => {
    const params = { page, page_size: PAGE_SIZE };
    
    // Quick filter (server-side)
    if (quickFilter) {
      params.quick_filter = quickFilter;
    }
    
    // Search
    if (searchQuery) {
      params.search = searchQuery;
    }
    
    // Multi-select filters (as comma-separated strings)
    if (filters.clients.length > 0) {
      params.clients = filters.clients.join(',');
    }
    if (filters.departments.length > 0) {
      params.departments = filters.departments.join(',');
    }
    if (filters.employees.length > 0) {
      params.employees = filters.employees.join(',');
    }
    if (filters.taskTypes.length > 0) {
      params.task_types = filters.taskTypes.join(',');
    }
    if (filters.subtypes.length > 0) {
      params.subtypes = filters.subtypes.join(',');
    }
    if (filters.statuses.length > 0) {
      params.statuses = filters.statuses.join(',');
    }
    if (filters.priorities.length > 0) {
      params.priorities = filters.priorities.join(',');
    }
    if (filters.dateFrom) {
      params.date_from = filters.dateFrom;
    }
    if (filters.dateTo) {
      params.date_to = filters.dateTo;
    }
    
    return params;
  };

  // Debounce search to avoid too many API calls
  const searchTimeoutRef = React.useRef(null);
  
  useEffect(() => { 
    fetchTaskTypes(); 
    fetchClients();
    fetchDepartments();
    fetchEmployees();
  }, []);

  // Fetch tasks when filters change (with debounce for search)
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchTasks(1, false);
    }, searchQuery ? 300 : 0); // Debounce search, immediate for other filters
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [quickFilter, searchQuery, filters]);

  // Update quick filter when initialFilter changes (from dashboard)
  useEffect(() => {
    if (initialFilter) {
      setQuickFilter(initialFilter);
    }
  }, [initialFilter]);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoadingMore && currentPage < totalPages) {
          loadMoreTasks();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );
    
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    
    return () => observer.disconnect();
  }, [currentPage, totalPages, isLoadingMore]);

  const fetchTasks = async (page = 1, append = false, { silent = false } = {}) => { 
    try {
      if (!silent) {
        if (page === 1) {
          setIsLoadingMore(false);
          setIsInitialLoad(true);
        } else {
          setIsLoadingMore(true);
        }
      }
      
      const params = buildQueryParams(page);
      const r = await adminApi.get('/api/v1/tasks', { params }); 
      const newTasks = r.data?.items || [];
      
      if (append) {
        setTasks(prev => [...prev, ...newTasks]);
      } else {
        setTasks(newTasks);
      }
      
      setTotalTasks(r.data?.total || 0);
      setTotalPages(r.data?.total_pages || 1);
      setCurrentPage(page);
      
      // Update server-side stats (only on first page to avoid redundant updates)
      if (page === 1 && r.data?.stats) {
        setServerStats(r.data.stats);
      }
    } catch (e) { 
      console.error('V1 API error, falling back to legacy:', e);
      try {
        const fallback = await adminApi.get('/api/admin/tasks');
        setTasks(fallback.data || []);
        setTotalTasks(fallback.data?.length || 0);
        setTotalPages(1);
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
      }
    } finally {
      setIsLoadingMore(false);
      setIsInitialLoad(false);
    }
  };
  
  const loadMoreTasks = () => {
    if (currentPage < totalPages && !isLoadingMore) {
      fetchTasks(currentPage + 1, true);
    }
  };
  const fetchTaskTypes = async () => { try { const r = await adminApi.get('/api/admin/task-types'); setTaskTypes(r.data || []); } catch (e) { console.error(e); } };
  const fetchClients = async () => { try { const r = await adminApi.get('/api/clients'); setClients(r.data || []); } catch (e) { console.error(e); } };
  const fetchDepartments = async () => { try { const r = await adminApi.get('/api/departments'); setDepartments(r.data || []); } catch (e) { console.error(e); } };
  const fetchEmployees = async () => { try { const r = await adminApi.get('/api/admin/employees'); setEmployees(r.data || []); } catch (e) { console.error(e); } };

  const handleCreateTask = async (d) => {
    try {
      setShowTaskModal(false);
      const res = await adminApi.post('/api/admin/tasks', d);
      toast.success('Task created');
      // Append new task to local state if server returned it
      if (res.data?._id) {
        setTasks(prev => [res.data, ...prev]);
        setTotalTasks(prev => prev + 1);
      } else {
        fetchTasks(1, false, { silent: true });
      }
      setPresetDueDate(null);
    } catch (e) { toast.error('Failed to create task'); }
  };
  const handleUpdateTask = async (d) => {
    const taskId = editingTask._id;
    // Optimistic update
    setTasks(prev => prev.map(t => t._id === taskId ? { ...t, ...d } : t));
    setShowTaskModal(false);
    setEditingTask(null);
    try {
      await adminApi.put(`/api/admin/tasks/${taskId}`, d);
      toast.success('Task updated');
    } catch (e) {
      toast.error('Failed to update task');
      fetchTasks(1, false, { silent: true }); // Rollback
    }
  };
  const handleInlineUpdate = async (id, d) => {
    // Optimistic update — instant visual change
    setTasks(prev => prev.map(t => t._id === id ? { ...t, ...d } : t));
    try {
      await adminApi.put(`/api/admin/tasks/${id}`, d);
      toast.success('Updated');
    } catch (e) {
      toast.error('Failed');
      fetchTasks(1, false, { silent: true }); // Rollback
    }
  };
  const handleStatusChange = async (id, status) => {
    // Optimistic status change — instant kanban move
    setTasks(prev => prev.map(t => t._id === id ? { ...t, status } : t));
    try {
      await adminApi.put(`/api/admin/tasks/${id}`, { status });
      toast.success(`Moved to ${status}`);
    } catch (e) {
      toast.error('Failed');
      fetchTasks(1, false, { silent: true }); // Rollback
    }
  };
  const handleDeleteTask = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    // Optimistic removal
    const previousTasks = tasks;
    setTasks(prev => prev.filter(t => t._id !== id));
    setTotalTasks(prev => prev - 1);
    try {
      await adminApi.delete(`/api/admin/tasks/${id}`);
      toast.success('Deleted');
    } catch (e) {
      toast.error('Failed');
      setTasks(previousTasks); // Rollback
      setTotalTasks(prev => prev + 1);
    }
  };
  const handleCreateTaskWithDate = (date) => { setPresetDueDate(date); setEditingTask(null); setShowTaskModal(true); };
  const handleDragStart = (e, task) => { e.dataTransfer.setData('taskId', task._id); e.dataTransfer.effectAllowed = 'move'; };
  const handleDrop = (taskId, newStatus) => handleStatusChange(taskId, newStatus);
  
  // Handle quick filter click
  const handleQuickFilterClick = (filterKey) => {
    setQuickFilter(filterKey === quickFilter ? null : filterKey);
    onClearFilter(); // Clear dashboard filter when user manually changes filter
  };
  
  // Get filter label for display
  const getFilterLabel = () => {
    const labels = {
      'all': 'All Active Tasks',
      'new': 'New Tasks',
      'inprogress': 'In Progress',
      'overdue': 'Overdue Tasks',
      'revision': 'Revision Tasks',
      'today': 'Due Today',
      'tomorrow': 'Due Tomorrow',
      'week': 'Due This Week',
      'high': 'High Priority',
      'critical': 'Critical Tasks'
    };
    return labels[quickFilter] || null;
  };

  // Task Type handlers
  const handleCreateTaskType = async () => { try { await adminApi.post('/api/admin/task-types', { name: taskTypeForm.name, subtypes: taskTypeForm.subtypes.filter(s => s.trim()) }); toast.success('Created'); fetchTaskTypes(); setShowTaskTypeModal(false); setTaskTypeForm({ name: '', subtypes: [''] }); } catch (e) { toast.error('Failed'); } };
  const handleUpdateTaskType = async () => { try { await adminApi.put(`/api/admin/task-types/${editingTaskType._id}`, { name: taskTypeForm.name, subtypes: taskTypeForm.subtypes.filter(s => s.trim()) }); toast.success('Updated'); fetchTaskTypes(); setShowTaskTypeModal(false); setEditingTaskType(null); setTaskTypeForm({ name: '', subtypes: [''] }); } catch (e) { toast.error('Failed'); } };
  const handleDeleteTaskType = async (id) => { if (!window.confirm('Delete?')) return; try { await adminApi.delete(`/api/admin/task-types/${id}`); toast.success('Deleted'); fetchTaskTypes(); } catch (e) { toast.error('Failed'); } };

  const statusColors = { 'New': 'bg-neutral-500 text-white', 'In Progress': 'bg-blue-500 text-white', 'Review': 'bg-purple-500 text-white', 'Revision': 'bg-yellow-500 text-white', 'Completed': 'bg-green-500 text-white' };
  const priorityColors = { 'Low': 'bg-gray-400 text-white', 'Medium': 'bg-blue-400 text-white', 'High': 'bg-orange-500 text-white', 'Critical': 'bg-red-600 text-white' };
  const kanbanColumns = [{ status: 'New', color: 'border-gray-400' }, { status: 'In Progress', color: 'border-blue-500' }, { status: 'Review', color: 'border-purple-500' }, { status: 'Revision', color: 'border-yellow-500' }, { status: 'Completed', color: 'border-green-500' }];
  
  // Date helpers for statistics display only
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const weekEnd = new Date(today);
  weekEnd.setDate(weekEnd.getDate() + 7);
  
  // Tasks are already filtered server-side, just use them directly
  const filteredTasks = tasks;

  return (
    <div className={isMobileView ? 'px-3 py-3 pb-20' : 'p-8'}>
      {/* Header - Mobile: Full width with stacked layout */}
      {isMobileView ? (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-lg font-bold text-[#1a1a2e]">Tasks</h1>
              <p className="text-xs text-neutral-500">Manage and track all tasks</p>
            </div>
            {activeTab === 'tasks' && (
              <button onClick={() => { setEditingTask(null); setPresetDueDate(null); setShowTaskModal(true); }} 
                className="px-3 py-2 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl text-sm font-medium flex items-center gap-1.5">
                <Plus className="w-4 h-4" /> New
              </button>
            )}
            {activeTab === 'task-types' && (
              <button onClick={() => { setEditingTaskType(null); setTaskTypeForm({ name: '', subtypes: [''] }); setShowTaskTypeModal(true); }} 
                className="px-3 py-2 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl text-sm font-medium flex items-center gap-1.5">
                <Plus className="w-4 h-4" /> New
              </button>
            )}
          </div>
          {/* Active Filter Badge - Mobile */}
          {quickFilter && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-neutral-500">Showing:</span>
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-pink-100 text-pink-800 rounded-full text-xs font-medium">
                {getFilterLabel()}
                <button onClick={() => { setQuickFilter(null); onClearFilter(); }} className="ml-1 hover:text-pink-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1a1a2e]">Tasks</h1>
            <p className="text-neutral-600 mt-1">Manage and track all tasks</p>
            {/* Active Filter Badge - Desktop */}
            {quickFilter && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-neutral-500">Filtered by:</span>
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-pink-100 text-pink-800 rounded-full text-sm font-medium">
                  {getFilterLabel()}
                  <button onClick={() => { setQuickFilter(null); onClearFilter(); }} className="ml-1 hover:text-pink-900">
                    <X className="w-4 h-4" />
                  </button>
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeTab === 'tasks' && (
              <button onClick={() => { setEditingTask(null); setPresetDueDate(null); setShowTaskModal(true); }} 
                className="px-4 py-2 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl hover:opacity-90 flex items-center gap-2">
                <Plus className="w-4 h-4" /> New Task
              </button>
            )}
            {activeTab === 'task-types' && (
              <button onClick={() => { setEditingTaskType(null); setTaskTypeForm({ name: '', subtypes: [''] }); setShowTaskTypeModal(true); }} 
                className="px-4 py-2 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl hover:opacity-90 flex items-center gap-2">
                <Plus className="w-4 h-4" /> New Task Type
              </button>
            )}
          </div>
        </div>
      )}

      {activeTab === 'tasks' && <TaskStatistics serverStats={serverStats} isMobile={isMobileView} activeFilter={quickFilter} onFilterClick={handleQuickFilterClick} />}

      {/* Tabs - Mobile: Compact */}
      <div className={`flex items-center border-b border-white/40 ${isMobileView ? 'mb-3' : 'gap-2 mb-6'}`}>
        <button onClick={() => setActiveTab('tasks')} 
          className={`font-medium border-b-2 transition-colors whitespace-nowrap ${isMobileView ? 'px-3 py-2 text-sm' : 'px-6 py-3'} ${activeTab === 'tasks' ? 'border-pink-600 text-pink-600' : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'}`}>
          All Tasks
        </button>
        <button onClick={() => setActiveTab('task-types')} 
          className={`font-medium border-b-2 transition-colors whitespace-nowrap ${isMobileView ? 'px-3 py-2 text-sm' : 'px-6 py-3'} ${activeTab === 'task-types' ? 'border-pink-600 text-pink-600' : 'border-transparent text-neutral-600 hover:text-[#1a1a2e]'}`}>
          Task Types
        </button>
      </div>

      {activeTab === 'tasks' && (
        <>
          {/* Search and View Controls - Mobile: Fully stacked */}
          {isMobileView ? (
            <div className="space-y-3 mb-4">
              {/* Search Bar with Filter Button */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input type="text" placeholder="Search tasks..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} 
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50" />
                </div>
                {/* Mobile Filter Button */}
                <TaskFilters 
                  filters={filters} 
                  onFiltersChange={setFilters} 
                  clients={clients} 
                  departments={departments} 
                  employees={employees} 
                  taskTypes={taskTypes} 
                  showClientFilter={true} 
                  accentColor="pink"
                  isMobile={true}
                />
              </div>
              
              {/* View Toggle - Horizontal buttons */}
              <div className="flex items-center gap-1 bg-neutral-100 rounded-xl p-1">
                {[
                  { key: 'list', icon: List, label: 'List' },
                  { key: 'kanban', icon: LayoutGrid, label: 'Board' },
                  { key: 'calendar', icon: CalendarIcon, label: 'Calendar' }
                ].map(v => (
                  <button key={v.key} onClick={() => setActiveView(v.key)} 
                    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-xs font-medium transition-colors ${
                      activeView === v.key 
                        ? 'bg-white text-pink-600 shadow-soft' 
                        : 'text-neutral-600 hover:text-[#1a1a2e]'
                    }`}>
                    <v.icon className="w-3.5 h-3.5" />
                    <span>{v.label}</span>
                  </button>
                ))}
              </div>
              
              {/* Results count */}
              <p className="text-xs text-neutral-500">
                Showing {filteredTasks.length} of {tasks.length} tasks
              </p>
            </div>
          ) : (
            <div className="flex items-start gap-4 mb-6">
              <div className="flex-1">
                <div className="relative mb-3">
                  <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input type="text" placeholder="Search tasks..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} 
                    className="w-full pl-10 pr-4 py-3 border border-white/40 rounded-xl focus:ring-2 focus:ring-brutal-pink/30" />
                </div>
                <TaskFilters filters={filters} onFiltersChange={setFilters} clients={clients} departments={departments}
                  employees={employees} taskTypes={taskTypes} showClientFilter={true} accentColor="pink" />
              </div>
              
              {/* View Toggle - Desktop */}
              <div className="flex items-center gap-1 glass-card rounded-xl p-1">
                {['list', 'kanban', 'calendar'].map(v => (
                  <button key={v} onClick={() => setActiveView(v)} 
                    className={`px-4 py-2 rounded-xl flex items-center gap-2 text-sm ${activeView === v ? 'bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white shadow-soft' : 'text-neutral-700 hover:bg-white/60'}`}>
                    {v === 'list' && <List className="w-4 h-4" />}
                    {v === 'kanban' && <LayoutGrid className="w-4 h-4" />}
                    {v === 'calendar' && <CalendarIcon className="w-4 h-4" />}
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results count - Desktop only (mobile shows it above) */}
          {!isMobileView && (
            <div className="text-sm text-neutral-500 mb-4 flex items-center gap-2">
              <span>Showing {tasks.length} of {totalTasks} tasks</span>
              {isInitialLoad && <span className="text-pink-500 animate-pulse">Loading...</span>}
            </div>
          )}

          {/* List View - Cards on Mobile, Table on Desktop */}
          {activeView === 'list' && (
            isMobileView ? (
              <div className="space-y-3">
                {isInitialLoad ? (
                  <div className="text-center text-neutral-500 py-12 glass-card">
                    <div className="animate-spin w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                    Loading tasks...
                  </div>
                ) : filteredTasks.length === 0 ? (
                  <div className="glass-card">
                    <EmptyState
                      icon={CheckCircle}
                      title="No tasks found"
                      description="Try adjusting your filters or create a new task."
                      tone="default"
                      testId="empty-tasks-mobile"
                    />
                  </div>
                ) : (
                  <>
                    {filteredTasks.map(task => (
                      <MobileTaskCard 
                        key={task._id} 
                        task={task} 
                        onEdit={(t) => { setEditingTask(t); setShowTaskModal(true); }}
                        onDelete={handleDeleteTask}
                        onStatusChange={handleStatusChange}
                        statusColors={statusColors}
                        priorityColors={priorityColors}
                        clients={clients}
                      />
                    ))}
                    {/* Infinite scroll trigger */}
                    <div ref={loadMoreRef} className="py-4 text-center">
                      {isLoadingMore && (
                        <div className="flex items-center justify-center gap-2 text-neutral-500">
                          <div className="animate-spin w-5 h-5 border-2 border-pink-500 border-t-transparent rounded-full"></div>
                          Loading more...
                        </div>
                      )}
                      {currentPage >= totalPages && tasks.length > 0 && (
                        <span className="text-xs text-neutral-400">All tasks loaded</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="soft-card overflow-hidden">
                <table className="w-full">
                  <thead className="bg-white/40 border-b border-white/40">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Task</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Client</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Due Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Assignee</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Priority</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/30">
                    {isInitialLoad ? (
                      <tr><td colSpan="7" className="px-6 py-12 text-center text-neutral-500">
                        <div className="animate-spin w-8 h-8 border-2 border-pink-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                        Loading tasks...
                      </td></tr>
                    ) : filteredTasks.length === 0 ? (
                      <tr><td colSpan="7" className="px-6 py-2">
                        <EmptyState
                          icon={CheckCircle}
                          title="No tasks found"
                          description="Try adjusting your filters or create a new task."
                          tone="default"
                          testId="empty-tasks-table"
                        />
                      </td></tr>
                    ) : (
                      filteredTasks.map(task => <TaskRow key={task._id} task={task} onOpenModal={(t) => { setEditingTask(t); setShowTaskModal(true); }} onInlineUpdate={handleInlineUpdate} onDelete={handleDeleteTask} statusColors={statusColors} priorityColors={priorityColors} clients={clients} />)
                    )}
                  </tbody>
                </table>
                {/* Infinite scroll trigger for table */}
                <div ref={loadMoreRef} className="py-4 text-center border-t border-white/30">
                  {isLoadingMore && (
                    <div className="flex items-center justify-center gap-2 text-neutral-500">
                      <div className="animate-spin w-5 h-5 border-2 border-pink-500 border-t-transparent rounded-full"></div>
                      Loading more tasks...
                    </div>
                  )}
                  {currentPage >= totalPages && tasks.length > 0 && !isLoadingMore && (
                    <span className="text-xs text-neutral-400">All {totalTasks} tasks loaded</span>
                  )}
                </div>
              </div>
            )
          )}

          {/* Kanban View - Horizontal scroll on mobile */}
          {activeView === 'kanban' && (
            <div className={isMobileView ? 'overflow-x-auto pb-4 -mx-4 px-4' : ''}>
              <div className={isMobileView ? 'flex gap-3 min-w-max' : 'grid grid-cols-5 gap-4'}>
                {kanbanColumns.map(col => (
                  <KanbanColumn 
                    key={col.status} 
                    status={col.status} 
                    color={col.color} 
                    tasks={filteredTasks} 
                    onEditTask={(t) => { setEditingTask(t); setShowTaskModal(true); }} 
                    onDeleteTask={handleDeleteTask} 
                    onDragStart={handleDragStart} 
                    onDrop={handleDrop}
                    isMobile={isMobileView}
                    statusColors={statusColors}
                    priorityColors={priorityColors}
                    clients={clients}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Calendar View */}
          {activeView === 'calendar' && (
            <TaskCalendarView 
              tasks={filteredTasks} 
              onEditTask={(t) => { setEditingTask(t); setShowTaskModal(true); }} 
              onCreateTask={handleCreateTaskWithDate}
              isMobile={isMobileView}
            />
          )}
        </>
      )}

      {/* Task Types Tab */}
      {activeTab === 'task-types' && (
        isMobileView ? (
          <div className="space-y-3">
            {taskTypes.map(type => (
              <div key={type._id} className="glass-card p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-[#1a1a2e]">{type.name}</h3>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { setEditingTaskType(type); setTaskTypeForm({ name: type.name, subtypes: type.subtypes || [''] }); setShowTaskTypeModal(true); }} 
                      className="text-pink-600 hover:text-pink-700 p-1"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => handleDeleteTaskType(type._id)} 
                      className="text-red-600 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(type.subtypes || []).map(s => (
                    <span key={s} className="px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded text-xs">{s}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="soft-card overflow-hidden">
            <table className="w-full">
              <thead className="bg-white/40 border-b border-white/40">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-neutral-700">Task Type</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-neutral-700">Subtypes</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-neutral-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/30">
                {taskTypes.map(type => (
                  <tr key={type._id} className="hover:bg-white/60">
                    <td className="px-6 py-4 font-medium text-[#1a1a2e]">{type.name}</td>
                    <td className="px-6 py-4"><div className="flex flex-wrap gap-1">{(type.subtypes || []).map(s => <span key={s} className="px-2 py-1 bg-neutral-100 text-neutral-700 rounded text-sm">{s}</span>)}</div></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => { setEditingTaskType(type); setTaskTypeForm({ name: type.name, subtypes: type.subtypes || [''] }); setShowTaskTypeModal(true); }} className="text-pink-600 hover:text-pink-700"><Edit className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteTaskType(type._id)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* Task Modal */}
      <TaskModal isOpen={showTaskModal} onClose={() => { setShowTaskModal(false); setEditingTask(null); setPresetDueDate(null); }}
        onSubmit={editingTask ? handleUpdateTask : handleCreateTask} onDelete={handleDeleteTask} api={adminApi} userType="admin" existingTask={editingTask} presetDueDate={presetDueDate} />

      {/* Task Type Modal */}
      {showTaskTypeModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className={`bg-white rounded-xl w-full ${isMobileView ? 'max-w-full mx-4' : 'max-w-md'}`}>
            <div className="flex items-center justify-between p-4 md:p-6 border-b">
              <h2 className="text-lg md:text-xl font-bold">{editingTaskType ? 'Edit' : 'Create'} Task Type</h2>
              <button onClick={() => setShowTaskTypeModal(false)} className="text-neutral-400 hover:text-neutral-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 md:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
                <input type="text" value={taskTypeForm.name} onChange={(e) => setTaskTypeForm({ ...taskTypeForm, name: e.target.value })} 
                  className="w-full px-4 py-2.5 md:py-3 border border-white/40 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Subtypes</label>
                {taskTypeForm.subtypes.map((s, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input type="text" value={s} onChange={(e) => { const newSubtypes = [...taskTypeForm.subtypes]; newSubtypes[i] = e.target.value; setTaskTypeForm({ ...taskTypeForm, subtypes: newSubtypes }); }} 
                      className="flex-1 px-4 py-2 border border-white/40 rounded-xl" />
                    {taskTypeForm.subtypes.length > 1 && (
                      <button onClick={() => setTaskTypeForm({ ...taskTypeForm, subtypes: taskTypeForm.subtypes.filter((_, idx) => idx !== i) })} 
                        className="text-red-500 p-2"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                <button onClick={() => setTaskTypeForm({ ...taskTypeForm, subtypes: [...taskTypeForm.subtypes, ''] })} 
                  className="text-pink-600 text-sm">+ Add Subtype</button>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button onClick={() => setShowTaskTypeModal(false)} 
                  className="px-4 py-2 md:px-6 md:py-3 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60">Cancel</button>
                <button onClick={editingTaskType ? handleUpdateTaskType : handleCreateTaskType} 
                  className="px-4 py-2 md:px-6 md:py-3 bg-gradient-to-r from-[#FF3366] to-[#FF3366] text-white rounded-xl hover:opacity-90">
                  {editingTaskType ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
